// Pools of curriculum-aligned chemistry worksheet content.
// Each pool is split by difficulty so generators can scale problems
// (not just shrink the element pool).

export type Difficulty = "easy" | "medium" | "hard";

// ---- Equation balancing ---------------------------------------------------
// easy   = 2 reactants, simple binary products
// medium = 3 species, common combustion / single replacement
// hard   = polyatomics, double replacement, multi-step
export const equationPools: Record<Difficulty, string[]> = {
  easy: [
    "H2 + O2 -> H2O",
    "Mg + O2 -> MgO",
    "Na + Cl2 -> NaCl",
    "K + Br2 -> KBr",
    "H2 + Cl2 -> HCl",
    "Ca + O2 -> CaO",
    "Li + O2 -> Li2O",
    "Al + O2 -> Al2O3",
  ],
  medium: [
    "Fe + O2 -> Fe2O3",
    "CH4 + O2 -> CO2 + H2O",
    "C3H8 + O2 -> CO2 + H2O",
    "Al + HCl -> AlCl3 + H2",
    "N2 + H2 -> NH3",
    "Zn + HCl -> ZnCl2 + H2",
    "KClO3 -> KCl + O2",
    "Ca + H2O -> Ca(OH)2 + H2",
    "Fe2O3 + CO -> Fe + CO2",
    "C2H6 + O2 -> CO2 + H2O",
  ],
  hard: [
    "Ca(OH)2 + H3PO4 -> Ca3(PO4)2 + H2O",
    "Al2(SO4)3 + Ca(OH)2 -> Al(OH)3 + CaSO4",
    "Pb(NO3)2 + KI -> PbI2 + KNO3",
    "Fe(OH)3 + H2SO4 -> Fe2(SO4)3 + H2O",
    "(NH4)2Cr2O7 -> N2 + Cr2O3 + H2O",
    "KMnO4 + HCl -> KCl + MnCl2 + Cl2 + H2O",
    "Ba(NO3)2 + Na2SO4 -> BaSO4 + NaNO3",
    "C8H18 + O2 -> CO2 + H2O",
    "NH3 + O2 -> NO + H2O",
    "Mg3N2 + H2O -> Mg(OH)2 + NH3",
  ],
};

// ---- Molar mass formulas --------------------------------------------------
// easy   = binary compounds
// medium = polyatomic ions, simple parentheses
// hard   = hydrates and large polyatomics
export interface MolarMassItem {
  formula: string;
  display: string; // human-readable form (e.g. with subscripts)
}

export const molarMassPools: Record<Difficulty, MolarMassItem[]> = {
  easy: [
    { formula: "H2O", display: "H₂O" },
    { formula: "NaCl", display: "NaCl" },
    { formula: "CO2", display: "CO₂" },
    { formula: "MgO", display: "MgO" },
    { formula: "HCl", display: "HCl" },
    { formula: "NH3", display: "NH₃" },
    { formula: "CaO", display: "CaO" },
    { formula: "K2O", display: "K₂O" },
    { formula: "FeO", display: "FeO" },
    { formula: "CH4", display: "CH₄" },
  ],
  medium: [
    { formula: "H2SO4", display: "H₂SO₄" },
    { formula: "Ca(OH)2", display: "Ca(OH)₂" },
    { formula: "C6H12O6", display: "C₆H₁₂O₆" },
    { formula: "Fe2O3", display: "Fe₂O₃" },
    { formula: "KMnO4", display: "KMnO₄" },
    { formula: "NaHCO3", display: "NaHCO₃" },
    { formula: "CaCO3", display: "CaCO₃" },
    { formula: "Mg(OH)2", display: "Mg(OH)₂" },
    { formula: "HNO3", display: "HNO₃" },
    { formula: "K2CrO4", display: "K₂CrO₄" },
  ],
  hard: [
    { formula: "Al2(SO4)3", display: "Al₂(SO₄)₃" },
    { formula: "(NH4)2SO4", display: "(NH₄)₂SO₄" },
    { formula: "CuSO4(H2O)5", display: "CuSO₄·5H₂O" },
    { formula: "MgSO4(H2O)7", display: "MgSO₄·7H₂O" },
    { formula: "Na2CO3(H2O)10", display: "Na₂CO₃·10H₂O" },
    { formula: "Ca3(PO4)2", display: "Ca₃(PO₄)₂" },
    { formula: "K2Cr2O7", display: "K₂Cr₂O₇" },
    { formula: "Fe(NO3)3(H2O)9", display: "Fe(NO₃)₃·9H₂O" },
    { formula: "(NH4)3PO4", display: "(NH₄)₃PO₄" },
    { formula: "Ba(ClO3)2", display: "Ba(ClO₃)₂" },
  ],
};

// ---- Significant figures --------------------------------------------------
export interface SigFigItem {
  number: string;
  count: number;
  reason: string;
}
export const sigFigPools: Record<Difficulty, SigFigItem[]> = {
  easy: [
    { number: "234", count: 3, reason: "all non-zero digits are significant" },
    { number: "45.6", count: 3, reason: "all non-zero digits are significant" },
    { number: "0.5", count: 1, reason: "leading zeros are not significant" },
    { number: "1200", count: 2, reason: "trailing zeros without a decimal are ambiguous; treated as 2" },
    { number: "78.0", count: 3, reason: "trailing zero after a decimal counts" },
  ],
  medium: [
    { number: "0.00450", count: 3, reason: "leading zeros ignored; trailing zero after decimal counts" },
    { number: "120.5", count: 4, reason: "captive zero counts; all digits significant" },
    { number: "0.060", count: 2, reason: "leading zeros ignored; trailing zero after decimal counts" },
    { number: "8000.", count: 4, reason: "decimal point makes trailing zeros significant" },
    { number: "5.020", count: 4, reason: "captive and trailing zero with decimal" },
  ],
  hard: [
    { number: "6.022 × 10²³", count: 4, reason: "only the coefficient counts in scientific notation" },
    { number: "1.50 × 10⁻³", count: 3, reason: "trailing zero in the coefficient counts" },
    { number: "0.000302", count: 3, reason: "leading zeros never count; captive zero does" },
    { number: "9.8000 × 10⁵", count: 5, reason: "all digits in the coefficient including trailing zeros" },
    { number: "1.0040 × 10²", count: 5, reason: "captive and trailing zero in the coefficient" },
  ],
};

// ---- Mole conversions -----------------------------------------------------
// At STP: 1 mol gas = 22.4 L.  1 mol = 6.022 × 10²³ particles.
export interface MoleConversionItem {
  question: string;
  answer: string;
}
export const moleConversionPools: Record<Difficulty, MoleConversionItem[]> = {
  easy: [
    {
      question: "How many moles are in 36 g of H₂O? (M = 18 g/mol)",
      answer: "2.0 mol",
    },
    {
      question: "How many grams are in 0.5 mol of NaCl? (M = 58.5 g/mol)",
      answer: "29.25 g",
    },
    {
      question: "How many moles are in 22.4 L of O₂ at STP?",
      answer: "1.0 mol",
    },
    {
      question: "How many liters does 2 mol of an ideal gas occupy at STP?",
      answer: "44.8 L",
    },
  ],
  medium: [
    {
      question: "How many moles are in 11.2 L of CO₂ gas at STP?",
      answer: "0.50 mol",
    },
    {
      question: "How many grams are in 0.25 mol of CaCO₃? (M = 100 g/mol)",
      answer: "25 g",
    },
    {
      question: "How many particles are in 0.50 mol of NH₃?",
      answer: "3.011 × 10²³ molecules",
    },
    {
      question: "How many moles are in 8.5 g of NH₃? (M = 17 g/mol)",
      answer: "0.50 mol",
    },
  ],
  hard: [
    {
      question:
        "A sample of N₂ gas occupies 5.6 L at STP. How many molecules of N₂ are present?",
      answer: "1.51 × 10²³ molecules",
    },
    {
      question:
        "How many grams of O₂ are needed to produce 6.022 × 10²³ molecules of CO₂ in: C + O₂ → CO₂?",
      answer: "32.0 g O₂",
    },
    {
      question:
        "What volume (at STP) does 88 g of CO₂ occupy? (M = 44 g/mol)",
      answer: "44.8 L",
    },
    {
      question:
        "How many atoms of oxygen are in 0.25 mol of Al₂(SO₄)₃?",
      answer: "1.81 × 10²⁴ O atoms",
    },
  ],
};

// ---- Oxidation numbers ----------------------------------------------------
// Each item asks for the ox# of the highlighted element in a compound.
export interface OxNumItem {
  formula: string;
  element: string;
  oxNumber: string; // e.g. "+6"
}
export const oxNumberPools: Record<Difficulty, OxNumItem[]> = {
  easy: [
    { formula: "NaCl", element: "Na", oxNumber: "+1" },
    { formula: "MgO", element: "Mg", oxNumber: "+2" },
    { formula: "AlCl₃", element: "Al", oxNumber: "+3" },
    { formula: "H₂O", element: "H", oxNumber: "+1" },
    { formula: "CaF₂", element: "F", oxNumber: "−1" },
    { formula: "K₂O", element: "K", oxNumber: "+1" },
  ],
  medium: [
    { formula: "H₂SO₄", element: "S", oxNumber: "+6" },
    { formula: "HNO₃", element: "N", oxNumber: "+5" },
    { formula: "KMnO₄", element: "Mn", oxNumber: "+7" },
    { formula: "K₂Cr₂O₇", element: "Cr", oxNumber: "+6" },
    { formula: "NH₃", element: "N", oxNumber: "−3" },
    { formula: "CaCO₃", element: "C", oxNumber: "+4" },
  ],
  hard: [
    { formula: "Na₂S₂O₃", element: "S", oxNumber: "+2 (avg.)" },
    { formula: "Fe₃O₄", element: "Fe", oxNumber: "+8/3 (avg.)" },
    { formula: "ClO₂⁻", element: "Cl", oxNumber: "+3" },
    { formula: "S₄O₆²⁻", element: "S", oxNumber: "+2.5 (avg.)" },
    { formula: "OF₂", element: "O", oxNumber: "+2" },
    { formula: "H₂O₂", element: "O", oxNumber: "−1" },
  ],
};

// ---- Naming ionic compounds ----------------------------------------------
// Pairs of (formula, name). Difficulty scales with polyatomics + multivalent metals.
export interface IonicNameItem {
  formula: string;
  name: string;
}
export const ionicNamePools: Record<Difficulty, IonicNameItem[]> = {
  easy: [
    { formula: "NaCl", name: "sodium chloride" },
    { formula: "KBr", name: "potassium bromide" },
    { formula: "MgO", name: "magnesium oxide" },
    { formula: "CaCl₂", name: "calcium chloride" },
    { formula: "Li₂O", name: "lithium oxide" },
    { formula: "AlF₃", name: "aluminium fluoride" },
    { formula: "BaS", name: "barium sulfide" },
    { formula: "K₃N", name: "potassium nitride" },
  ],
  medium: [
    { formula: "Na₂SO₄", name: "sodium sulfate" },
    { formula: "KNO₃", name: "potassium nitrate" },
    { formula: "Ca(OH)₂", name: "calcium hydroxide" },
    { formula: "NH₄Cl", name: "ammonium chloride" },
    { formula: "Mg(NO₃)₂", name: "magnesium nitrate" },
    { formula: "Al₂(SO₄)₃", name: "aluminium sulfate" },
    { formula: "K₂CO₃", name: "potassium carbonate" },
    { formula: "NaHCO₃", name: "sodium bicarbonate" },
  ],
  hard: [
    { formula: "FeCl₃", name: "iron(III) chloride" },
    { formula: "CuSO₄", name: "copper(II) sulfate" },
    { formula: "Pb(NO₃)₂", name: "lead(II) nitrate" },
    { formula: "Fe₂(SO₄)₃", name: "iron(III) sulfate" },
    { formula: "Sn(OH)₄", name: "tin(IV) hydroxide" },
    { formula: "(NH₄)₃PO₄", name: "ammonium phosphate" },
    { formula: "Cr₂(CO₃)₃", name: "chromium(III) carbonate" },
    { formula: "Hg₂Cl₂", name: "mercury(I) chloride" },
  ],
};

// ---- Lewis structures (text answer key, not drawing) ----------------------
// Asks for the count of bonding pairs / lone pairs around the central atom.
export interface LewisItem {
  molecule: string;
  question: string;
  answer: string;
}
export const lewisPools: Record<Difficulty, LewisItem[]> = {
  easy: [
    { molecule: "H₂", question: "How many bonding pairs and lone pairs are in H₂?", answer: "1 bonding pair, 0 lone pairs" },
    { molecule: "Cl₂", question: "How many lone pairs are on each Cl atom in Cl₂?", answer: "3 lone pairs per Cl" },
    { molecule: "HCl", question: "How many bonding pairs in HCl?", answer: "1 bonding pair, 3 lone pairs on Cl" },
    { molecule: "H₂O", question: "How many lone pairs are on the central O in H₂O?", answer: "2 lone pairs" },
    { molecule: "NH₃", question: "How many lone pairs are on the central N in NH₃?", answer: "1 lone pair" },
    { molecule: "CH₄", question: "How many bonding pairs around the central C in CH₄?", answer: "4 bonding pairs, 0 lone pairs" },
  ],
  medium: [
    { molecule: "CO₂", question: "Describe the bonds around C in CO₂.", answer: "2 double bonds (C=O), 0 lone pairs on C" },
    { molecule: "N₂", question: "What type of bond is between the N atoms in N₂?", answer: "Triple bond; 1 lone pair on each N" },
    { molecule: "O₂", question: "What type of bond is between the O atoms in O₂?", answer: "Double bond; 2 lone pairs on each O" },
    { molecule: "PCl₃", question: "How many lone pairs on the central P in PCl₃?", answer: "1 lone pair" },
    { molecule: "H₂S", question: "How many lone pairs on the central S in H₂S?", answer: "2 lone pairs" },
  ],
  hard: [
    { molecule: "SO₂", question: "Describe the Lewis structure of SO₂ (with resonance).", answer: "1 single + 1 double bond (resonance); 1 lone pair on S" },
    { molecule: "NO₃⁻", question: "Describe the Lewis structure of NO₃⁻.", answer: "3 N–O bonds (1 double, 2 single, resonance); 0 lone pairs on N; −1 charge delocalized" },
    { molecule: "SF₆", question: "How many bonding pairs around S in SF₆?", answer: "6 bonding pairs, 0 lone pairs (octahedral, expanded octet)" },
    { molecule: "XeF₄", question: "Describe Xe in XeF₄.", answer: "4 bonding pairs + 2 lone pairs (square planar, expanded octet)" },
    { molecule: "ClF₃", question: "Describe Cl in ClF₃.", answer: "3 bonding pairs + 2 lone pairs (T-shaped, expanded octet)" },
  ],
};

// ---- Stoichiometry --------------------------------------------------------
// Mass-mass and mass-mole problems built around a balanced equation.
// easy   = mole-mole ratios from a balanced equation
// medium = mass ↔ mole conversions across a balanced equation
// hard   = mass-mass problems, limiting reagent setups, multi-step
export interface StoichItem {
  equation: string; // balanced equation displayed to the student
  question: string;
  answer: string;
}

export const stoichiometryPools: Record<Difficulty, StoichItem[]> = {
  easy: [
    {
      equation: "2H₂ + O₂ → 2H₂O",
      question: "How many moles of H₂O form from 4 mol of H₂ (excess O₂)?",
      answer: "4 mol H₂O (1:1 ratio between H₂ and H₂O)",
    },
    {
      equation: "N₂ + 3H₂ → 2NH₃",
      question: "How many moles of NH₃ form from 6 mol of H₂ (excess N₂)?",
      answer: "4 mol NH₃ (6 mol H₂ × 2/3)",
    },
    {
      equation: "2Na + Cl₂ → 2NaCl",
      question: "How many moles of Cl₂ are needed to react with 5 mol of Na?",
      answer: "2.5 mol Cl₂ (5 mol Na × 1/2)",
    },
    {
      equation: "CH₄ + 2O₂ → CO₂ + 2H₂O",
      question: "How many moles of O₂ are needed to burn 3 mol of CH₄?",
      answer: "6 mol O₂ (3 mol × 2)",
    },
    {
      equation: "2KClO₃ → 2KCl + 3O₂",
      question: "How many moles of O₂ form from 4 mol of KClO₃?",
      answer: "6 mol O₂ (4 mol × 3/2)",
    },
  ],
  medium: [
    {
      equation: "2H₂ + O₂ → 2H₂O",
      question:
        "How many grams of H₂O form from 4.0 g of H₂ (excess O₂)? (M(H₂)=2, M(H₂O)=18)",
      answer: "36 g H₂O (4.0/2 = 2 mol H₂ → 2 mol H₂O × 18)",
    },
    {
      equation: "CH₄ + 2O₂ → CO₂ + 2H₂O",
      question:
        "How many moles of CO₂ form from 32 g of CH₄? (M(CH₄)=16)",
      answer: "2.0 mol CO₂ (32/16 = 2 mol CH₄, 1:1 with CO₂)",
    },
    {
      equation: "N₂ + 3H₂ → 2NH₃",
      question:
        "How many grams of NH₃ form from 2.0 mol of N₂? (M(NH₃)=17)",
      answer: "68 g NH₃ (2.0 × 2 = 4.0 mol NH₃ × 17)",
    },
    {
      equation: "2Al + 3Cl₂ → 2AlCl₃",
      question:
        "How many moles of AlCl₃ form from 27 g of Al? (M(Al)=27)",
      answer: "1.0 mol AlCl₃ (27/27 = 1 mol Al, 1:1 with AlCl₃)",
    },
    {
      equation: "Zn + 2HCl → ZnCl₂ + H₂",
      question:
        "How many liters of H₂ at STP form from 0.50 mol of Zn?",
      answer: "11.2 L H₂ (0.50 mol × 22.4 L/mol)",
    },
  ],
  hard: [
    {
      equation: "2C₈H₁₈ + 25O₂ → 16CO₂ + 18H₂O",
      question:
        "How many grams of CO₂ form from 114 g of C₈H₁₈ (octane)? (M(C₈H₁₈)=114, M(CO₂)=44)",
      answer:
        "352 g CO₂ (114/114 = 1 mol C₈H₁₈ → 8 mol CO₂ × 44)",
    },
    {
      equation: "Fe₂O₃ + 3CO → 2Fe + 3CO₂",
      question:
        "How many grams of Fe form from 160 g of Fe₂O₃? (M(Fe₂O₃)=160, M(Fe)=56)",
      answer:
        "112 g Fe (160/160 = 1 mol Fe₂O₃ → 2 mol Fe × 56)",
    },
    {
      equation: "N₂ + 3H₂ → 2NH₃",
      question:
        "Limiting reagent: 28 g N₂ reacts with 4.0 g H₂. Which is limiting and how many g NH₃ form? (M(N₂)=28, M(H₂)=2, M(NH₃)=17)",
      answer:
        "H₂ limiting (need 6 g for 28 g N₂; only 4 g present). 4/2 = 2 mol H₂ → 2×(2/3) = 1.33 mol NH₃ → 22.7 g NH₃",
    },
    {
      equation: "2Al + 3CuSO₄ → Al₂(SO₄)₃ + 3Cu",
      question:
        "How many grams of Cu form from 5.4 g of Al (excess CuSO₄)? (M(Al)=27, M(Cu)=63.5)",
      answer:
        "19.05 g Cu (5.4/27 = 0.20 mol Al → 0.30 mol Cu × 63.5)",
    },
    {
      equation: "4NH₃ + 5O₂ → 4NO + 6H₂O",
      question:
        "How many liters of NO at STP form from 34 g of NH₃? (M(NH₃)=17)",
      answer:
        "44.8 L NO (34/17 = 2.0 mol NH₃ → 2.0 mol NO × 22.4 L/mol)",
    },
  ],
};
